// PaymentReceipt.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { getFirestore, doc, getDoc } from "firebase/firestore";

// ========== Helpers de Retry y Utils ==========
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withRetry(requestFn, maxAttempts = 3, baseDelay = 2000) {
  let lastErr;
  for (let i = 1; i <= maxAttempts; i++) {
    try {
      return await requestFn();
    } catch (err) {
      lastErr = err;
      const isLast = i === maxAttempts;
      if (isLast) break;
      const jitter = Math.floor(Math.random() * 500);
      await sleep(baseDelay * i + jitter); // 2s, 4s, 6s (+jitter)
    }
  }
  throw lastErr;
}

// Normalizador de teléfono MX a 521XXXXXXXXXX (JID sin @)
function toWhatsAppJid(phone) {
  let num = String(phone).replace(/\D/g, "");
  if (num.length === 10) return "521" + num;
  if (num.length === 12 && num.startsWith("52") && num[2] !== "1")
    return "521" + num.slice(2);
  if (num.length === 13 && num.startsWith("521")) return num;
  return num;
}

const PaymentReceipt = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state || {};

  // Dummy para pruebas locales; en producción espera `location.state.transaction`
  const dummyTransaction = {
    businessPhone: "8311760335",
    businessName: "Mi Negocio de Prueba",
    receiptUrl: "https://via.placeholder.com/300",
    agentName: "Sergio",
    ownerName: "Dueño Prueba",
    date: new Date().toISOString(),
    dayAmount: 20.0,
    totalDebt: 200.0,
    totalAmount: 50.0,
    tipo: "pago", // "adeudo" | "abono" | "pago"
  };

  const transaction = locationState.transaction || dummyTransaction;

  const [alert, setAlert] = useState(null); // {type: 'success'|'error'|'info', message: string}
  const [isSending, setIsSending] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const sentRef = useRef(false);

  const db = getFirestore();

  const formatDateMx = (dateValue) => {
    const date = dateValue?.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("es-MX");
  };

  const fetchWhatsAppConfig = async () => {
    const configRef = doc(db, "config", "whatsapp");
    const configSnap = await getDoc(configRef);
    if (!configSnap.exists()) return null;
    const data = configSnap.data() || {};
    return {
      apiToken: data.wa_api_token || "",
      phoneId: data.wa_phone_id || "",
      templateName: data.wa_template_name || "",
      templateLang: data.wa_template_lang || "es_MX",
      sendMode: data.wa_send_mode || "qr",
    };
  };

  const sendCloudTemplate = async ({ config, phone, imageUrl, bodyParams }) => {
    const payload = {
      messaging_product: "whatsapp",
      to: phone,
      type: "template",
      template: {
        name: config.templateName,
        language: { code: config.templateLang || "es_MX" },
        components: [
          {
            type: "body",
            parameters: bodyParams.map((value) => ({
              type: "text",
              text: String(value ?? ""),
            })),
          },
        ],
      },
    };

    if (imageUrl) {
      payload.template.components.unshift({
        type: "header",
        parameters: [
          {
            type: "image",
            image: { link: imageUrl },
          },
        ],
      });
    }

    const url = `https://graph.facebook.com/v21.0/${config.phoneId}/messages`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${config.apiToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errPayload = await response.json().catch(() => ({}));
      throw new Error(errPayload?.error?.message || "Error al enviar plantilla.");
    }

    return response.json().catch(() => ({}));
  };

  const handleSendReceiptWhatsApp = async () => {
    try {
      if (!transaction || !transaction.receiptUrl) {
        setAlert({ type: "error", message: "No se encontró la URL del comprobante." });
        return;
      }

      setIsSending(true);
      setAlert({ type: "info", message: "Enviando por WhatsApp..." });

      const phoneFormatted = toWhatsAppJid(transaction.businessPhone);
      if (!phoneFormatted || phoneFormatted.length < 12) {
        setAlert({
          type: "error",
          message: "Telefono del negocio invalido. Debe tener 10 digitos.",
        });
        return;
      }
      const bodyParams = [
        transaction.ownerName || transaction.businessName || "",
        transaction.businessName || "",
        formatDateMx(transaction.date),
      ];

      const config = await fetchWhatsAppConfig();
      if (!config) {
        setAlert({ type: "error", message: "No hay configuracion de WhatsApp." });
        return;
      }
      if (!config.apiToken || !config.phoneId || !config.templateName) {
        setAlert({ type: "error", message: "Falta configurar WhatsApp API." });
        return;
      }

      if (config.sendMode === "qr") {
        setAlert({
          type: "error",
          message: "El modo QR requiere backend. Cambia a plantilla o aleatorio.",
        });
        return;
      }

      const chosenMode =
        config.sendMode === "random" && Math.random() < 0.5 ? "qr" : "template";

      if (chosenMode === "qr") {
        setAlert({
          type: "info",
          message: "Modo aleatorio eligio QR, se enviara por plantilla.",
        });
      }

      await withRetry(
        () =>
          sendCloudTemplate({
            config,
            phone: phoneFormatted,
            imageUrl: transaction.receiptUrl,
            bodyParams,
          }),
        3,
        2000
      );

      setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
      setHasSentWhatsapp(true);
      sentRef.current = true;
    } catch (error) {
      console.error("Error al enviar recibo por WhatsApp:", error);
      setAlert({
        type: "error",
        message: `Error al enviar por WhatsApp: ${error?.message || "verifica la plantilla y parametros."}`,
      });
    } finally {
      setIsSending(false);
    }
  };

  // Envío automático UNA sola vez al cargar, si hay receiptUrl
  useEffect(() => {
    if (transaction && transaction.receiptUrl && !sentRef.current) {
      sentRef.current = true; // evita doble envío en re-render
      handleSendReceiptWhatsApp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.receiptUrl]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#861E3D]">
      {/* Encabezado con botón para regresar */}
      <header className="w-full p-4 flex items-center">
        <button
          onClick={() => navigate("/cobrador")}
          className="bg-gray-300 text-gray-700 p-2 rounded-full hover:bg-gray-400"
        >
          <FaArrowLeft size={20} />
        </button>
      </header>

      {/* Título acorde al tipo de transacción */}
      <h1 className="text-2xl font-bold text-white">
        {transaction.tipo === "adeudo"
          ? "Notificación de Adeudo"
          : transaction.tipo === "abono"
          ? "Notificación de Abono"
          : "Recibo de Pago"}
      </h1>

      {/* Imagen del comprobante */}
      {transaction.receiptUrl && (
        <img
          src={transaction.receiptUrl}
          alt="Comprobante"
          className="mt-4 max-w-sm border-2 border-gray-300 rounded mb-6"
        />
      )}

      {/* Botón para reenviar manualmente por WhatsApp */}
      <button
        onClick={handleSendReceiptWhatsApp}
        className="bg-orange-500 text-white px-6 py-3 rounded-lg mt-2 disabled:opacity-60"
        disabled={isSending}
      >
        {isSending ? "Enviando..." : "Reenviar mensaje por WhatsApp"}
      </button>

      {/* Mensaje de estado */}
      {alert && (
        <div
          className={`mt-4 p-4 rounded text-white ${
            alert.type === "success"
              ? "bg-green-500"
              : alert.type === "info"
              ? "bg-blue-500"
              : "bg-red-500"
          }`}
        >
          {alert.message}
        </div>
      )}
    </div>
  );
};

export default PaymentReceipt;
